/**
 * Rehydrating a service selection — used when resuming a parked draft.
 *
 * `newServiceForm` keeps its picks in its OWN state (`selectedServices`) and
 * pushes them up to the wizard, which is what gets written into the draft. On
 * resume the wizard restores them and passes them back down as `props.services`,
 * but the form's state starts empty, so the Work step renders no selections at
 * all. The catalog half of this was already fixed in componentDidMount; this is
 * the other half.
 *
 * `_uid` is load-bearing, not decoration: removal, per-line discounts, the
 * product picker and the marine area calculator all locate a row by it. A
 * restored row without one — or one that collides with the next generated id —
 * makes those act on the wrong line. So restoration backfills what is missing
 * and advances the counter past every `cN` it has seen.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/serviceSelection.test.mjs`. Do not add imports.
 */

/**
 * @param {Array} services  rows from the draft (props.services)
 * @param {number} startId  the form's current _nextClientId
 * @returns {{ selectedServices: Array, nextClientId: number }}
 *
 * Rows are copied, never mutated — the caller's props must not be rewritten.
 */
export function restoreSelectedServices(services, startId = 1) {
  if (!Array.isArray(services)) {
    return { selectedServices: [], nextClientId: startId };
  }

  const rows = services.filter((s) => s && typeof s === 'object');

  // Clear every client id already in use. Only `cN` counters participate — a
  // real service id of 9001 says nothing about the next client id.
  let next = startId;
  rows.forEach((s) => {
    const m = /^c(\d+)$/.exec(String(s._uid ?? ''));
    if (m) next = Math.max(next, Number(m[1]) + 1);
  });

  const selectedServices = rows.map((s) => {
    if (s._uid !== undefined && s._uid !== null && s._uid !== '') return { ...s };
    if (s.id !== undefined && s.id !== null && s.id !== '') return { ...s, _uid: s.id };
    return { ...s, _uid: `c${next++}` };
  });

  return { selectedServices, nextClientId: next };
}
